/**
 * @module lib/commercial/ad-security
 */
import { mark, measure, getEntriesByName } from "lib/performance/user-timing";
import crow from "utils/crow";
import logger from "utils/logger";
import initConfiant from "./confiant";

const debugTag = "[AD-SECURITY]";

/**
 * Dynamically load an ad security script, if applicable
 * @returns {Promise}
 */
export default function initAdSecurity() {
  mark("ad_security_confiant_started");
  return initConfiant()
    .then(() => {
      logger.info(`${debugTag} Successfully loaded Confiant script`);
    })
    .catch(() => {
      crow.error(new Error("Failed to load Confiant script"));
    })
    .then(() => {
      mark("ad_security_confiant_done");
      measure("ad_security_confiant");
      getDurationAndPushToAnalytics("confiant");
    });
}

/**
 * Determine whether the ad security script should execute on the current page
 * @param {String} urlRelative - The relative URL of the current page
 * @param {String} enabledSections - A comma-separated list of sections where we want the ad security script to run
 * @returns {Boolean}
 */
export function adSecurityShouldRun(urlRelative, enabledSections) {
  if (!urlRelative || !enabledSections) {
    return false;
  }
  if (enabledSections === "*") {
    logger.info(`${debugTag} Enabled for: *`);
    return true;
  }
  const matchingSectionExists = enabledSections.split(",").some(sec => {
    let section = sec.trim();
    section = section.startsWith("/") ? section : `/${section}`;
    section = section.endsWith("/") ? section.slice(0, -1) : section;
    const matchFound = urlRelative.startsWith(section);
    if (matchFound) {
      logger.info(`${debugTag} Enabled for: ${section}`);
    }
    return matchFound;
  });
  return matchingSectionExists;
}

/**
 * Determine the duration for which a given ad tech script ran, send this info to GTM
 * @param {String} entryName - label given to event we would like to time
 */
function getDurationAndPushToAnalytics(entryName) {
  const entries = getEntriesByName(`ad_security_${entryName}`);
  if (!entries.length) {
    return;
  }
  const duration = parseInt(entries[0].duration, 10);
  logger.info(`${entryName} took ${duration}ms to execute`);
  // Only 10% of total page views will send their performace data to GTM
  const sample = parseInt(100 * Math.random(), 10);
  if (sample <= 10) {
    window.analyticsLayer.push({
      event: "simpleEvent",
      simpleEvent: {
        category: "advertising",
        action: "performance",
        label: `${entryName} ad security performance`,
        value: duration
      }
    });
  }
}
